// Hook for centralized drag & drop logic in timeline components
// CHANGE: Converted lastDragOverUpdate and touchStartY from useState to useRef
//         to avoid unnecessary re-renders during drag operations
// FIX: Added isDropPendingRef to prevent useEffect from clearing drag state during active drags
// FIX: Added draggedTaskRef to prevent stale closures in handleDragOver

import { useState, useRef, useEffect, useCallback } from 'react';
import { createInvisibleDragImage, calculateDragOffset } from '../lib/dragDropUtils';
import { roundToQuarterHour, minutesToTime, timeToMinutes } from '../lib/timeCalculations';

export interface TimelineTask {
  id: string;
  title: string;
  startTime: string;
  duration: number;
  status: 'not_started' | 'completed' | 'overdue';
  operatorName: string;
  operatorUsername: string;
  operatorId?: string;
  isRecurring?: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
  serviceColor?: string;
  serviceName?: string;
}

export interface DragPosition {
  time: string;
  operatorId: string;
}

interface UseDragDropTimelineProps {
  tasks: TimelineTask[];
  onTaskDrop?: (taskId: string, fromOperatorId: string, toOperatorId: string, task: TimelineTask, newScheduledTime: string) => void;
  rollbackRef?: React.MutableRefObject<(() => void) | null>;
}

interface UseDragDropTimelineReturn {
  draggedTask: TimelineTask | null;
  draggedTaskPosition: DragPosition | null;
  isDragRollback: boolean;
  isTouchDragging: boolean;
  dragStartOffset: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  handleDragStart: (e: React.DragEvent, task: TimelineTask) => void;
  handleDragEnd: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent, operatorId: string, timelineElement: HTMLElement | null) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, toOperatorId: string) => void;
  handleTouchStart: (e: React.TouchEvent, task: TimelineTask, operatorId: string) => void;
  handleTouchMove: (e: React.TouchEvent, operatorId: string, timelineElement: HTMLElement | null) => void;
  handleTouchEnd: (e: React.TouchEvent, operatorId: string) => void;
  handleTouchCancel: (e: React.TouchEvent) => void;
  canReassignTask: (task: TimelineTask) => boolean;
  rollbackDraggedTask: () => void;
}

export function useDragDropTimeline({
  tasks,
  onTaskDrop,
  rollbackRef
}: UseDragDropTimelineProps): UseDragDropTimelineReturn {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [draggedTask, setDraggedTask] = useState<TimelineTask | null>(null);
  const [draggedTaskPosition, setDraggedTaskPosition] = useState<DragPosition | null>(null);
  const draggedTaskRef = useRef<TimelineTask | null>(null);
  const draggedTaskPositionRef = useRef<DragPosition | null>(null);
  const isDropPendingRef = useRef(false);
  const lastDragOverUpdateRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const touchTimeoutRef = useRef<number>();
  const [dragStartOffset, setDragStartOffset] = useState<number>(0);
  const [isDragRollback, setIsDragRollback] = useState(false);
  const [originalDragPosition, setOriginalDragPosition] = useState<DragPosition | null>(null);

  const calculateTimeFromY = useCallback((
    clientY: number,
    timelineElement: HTMLElement | null,
    offset: number = 0
  ): string => {
    if (!timelineElement) {
      return '08:00';
    }

    const timelineRect = timelineElement.getBoundingClientRect();
    const relativeY = clientY - timelineRect.top - offset;
    const totalMinutes = Math.max(0, Math.min(relativeY, 24 * 60));
    const roundedMinutes = roundToQuarterHour(totalMinutes);

    return minutesToTime(roundedMinutes);
  }, []);

  const canReassignTask = useCallback((task: TimelineTask): boolean => {
    return task.status !== 'completed';
  }, []);

  const rollbackDraggedTask = useCallback(() => {
    if (!draggedTask || !originalDragPosition) return;

    isDropPendingRef.current = false;
    setIsDragRollback(true);
    setDraggedTaskPosition(originalDragPosition);

    setTimeout(() => {
      draggedTaskRef.current = null;
      draggedTaskPositionRef.current = null;
      setDraggedTask(null);
      setDraggedTaskPosition(null);
      setOriginalDragPosition(null);
      setDragStartOffset(0);
      setIsDragRollback(false);
      setIsTouchDragging(false);
    }, 400);
  }, [draggedTask, originalDragPosition]);

  useEffect(() => {
    if (!isDropPendingRef.current) return;

    const currentDraggedTask = draggedTaskRef.current;
    const currentPosition = draggedTaskPositionRef.current;
    if (!currentDraggedTask || !currentPosition) return;

    const updatedTask = tasks.find(t => t.id === currentDraggedTask.id);

    if (!updatedTask) {
      isDropPendingRef.current = false;
      setDraggedTask(null);
      setDraggedTaskPosition(null);
      setDragStartOffset(0);
      setOriginalDragPosition(null);
    } else {
      const positionMatches =
        updatedTask.operatorId === currentPosition.operatorId &&
        updatedTask.startTime === currentPosition.time;

      if (positionMatches) {
        isDropPendingRef.current = false;
        setDraggedTask(null);
        setDraggedTaskPosition(null);
        setDragStartOffset(0);
        setOriginalDragPosition(null);
      }
    }
  }, [tasks]);

  useEffect(() => {
    if (rollbackRef) {
      rollbackRef.current = rollbackDraggedTask;
    }
    return () => {
      if (rollbackRef) {
        rollbackRef.current = null;
      }
    };
  }, [rollbackDraggedTask, rollbackRef]);

  const handleDragStart = useCallback((e: React.DragEvent, task: TimelineTask) => {
    if (!canReassignTask(task)) {
      e.preventDefault();
      return;
    }
    draggedTaskRef.current = task;
    draggedTaskPositionRef.current = null;
    isDropPendingRef.current = false;
    setDraggedTask(task);
    setOriginalDragPosition({
      time: task.startTime,
      operatorId: task.operatorId || ''
    });
    setIsDragRollback(false);

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('taskId', task.id);
    e.dataTransfer.setData('operatorId', task.operatorId || '');

    const taskElement = e.currentTarget as HTMLElement;
    const offset = calculateDragOffset(e, taskElement);
    setDragStartOffset(offset);

    createInvisibleDragImage(e);
  }, [canReassignTask]);

  const handleDragEnd = useCallback(() => {
    if (!isDropPendingRef.current) {
      draggedTaskRef.current = null;
      draggedTaskPositionRef.current = null;
    }
    setDraggedTask(null);
    setDraggedTaskPosition(null);
    setDragStartOffset(0);
  }, []);

  const handleDragOver = useCallback((
    e: React.DragEvent,
    operatorId: string,
    timelineElement: HTMLElement | null
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedTaskRef.current) return;

    const currentPosition = draggedTaskPositionRef.current;
    const now = Date.now();
    const shouldUpdateTime = now - lastDragOverUpdateRef.current >= 50;

    if (shouldUpdateTime) {
      lastDragOverUpdateRef.current = now;
      const newTime = calculateTimeFromY(e.clientY, timelineElement, dragStartOffset);
      const newPosition = { time: newTime, operatorId };
      draggedTaskPositionRef.current = newPosition;
      setDraggedTaskPosition(newPosition);
    } else if (currentPosition?.operatorId !== operatorId) {
      const currentTime = currentPosition?.time || draggedTaskRef.current.startTime;
      const newPosition = { time: currentTime, operatorId };
      draggedTaskPositionRef.current = newPosition;
      setDraggedTaskPosition(newPosition);
    }
  }, [dragStartOffset, calculateTimeFromY]);

  const handleDragLeave = useCallback(() => {
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toOperatorId: string) => {
    e.preventDefault();

    const taskId = e.dataTransfer.getData('taskId');
    const fromOperatorId = e.dataTransfer.getData('operatorId');
    const currentTask = draggedTaskRef.current;
    const currentPosition = draggedTaskPositionRef.current;

    if (!taskId || !currentTask || !currentPosition) {
      draggedTaskRef.current = null;
      draggedTaskPositionRef.current = null;
      setDraggedTask(null);
      setDraggedTaskPosition(null);
      return;
    }

    isDropPendingRef.current = true;

    if (onTaskDrop) {
      onTaskDrop(taskId, fromOperatorId, toOperatorId, currentTask, currentPosition.time);
    }
  }, [onTaskDrop]);

  const handleTouchStart = useCallback((
    e: React.TouchEvent,
    task: TimelineTask,
    operatorId: string
  ) => {
    if (!canReassignTask(task)) return;

    const touch = e.touches[0];
    touchStartYRef.current = touch.clientY;

    const taskElement = e.currentTarget as HTMLElement;
    const offset = calculateDragOffset(e, taskElement);

    touchTimeoutRef.current = window.setTimeout(() => {
      draggedTaskRef.current = task;
      draggedTaskPositionRef.current = null;
      isDropPendingRef.current = false;
      setIsTouchDragging(true);
      setDraggedTask(task);
      setOriginalDragPosition({
        time: task.startTime,
        operatorId: task.operatorId || ''
      });
      setIsDragRollback(false);
      setDragStartOffset(offset);

      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    }, 500);
  }, [canReassignTask]);

  const handleTouchMove = useCallback((
    e: React.TouchEvent,
    operatorId: string,
    timelineElement: HTMLElement | null
  ) => {
    if (!isTouchDragging || !draggedTaskRef.current) return;

    e.preventDefault();

    const touch = e.touches[0];
    const now = Date.now();
    if (now - lastDragOverUpdateRef.current < 50) return;
    lastDragOverUpdateRef.current = now;

    const newTime = calculateTimeFromY(touch.clientY, timelineElement, dragStartOffset);
    const newPosition = { time: newTime, operatorId };
    draggedTaskPositionRef.current = newPosition;
    setDraggedTaskPosition(newPosition);

    const prevPosition = draggedTaskPositionRef.current;
    const prevTime = prevPosition ? timeToMinutes(prevPosition.time) : 0;
    const currentTime = timeToMinutes(newTime);
    if (Math.floor(prevTime / 15) !== Math.floor(currentTime / 15)) {
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }
    }
  }, [isTouchDragging, dragStartOffset, calculateTimeFromY]);

  const handleTouchEnd = useCallback((e: React.TouchEvent, operatorId: string) => {
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }

    const currentTask = draggedTaskRef.current;
    const currentPosition = draggedTaskPositionRef.current;

    if (!isTouchDragging || !currentTask || !currentPosition) {
      draggedTaskRef.current = null;
      draggedTaskPositionRef.current = null;
      setIsTouchDragging(false);
      setDraggedTask(null);
      setDraggedTaskPosition(null);
      setDragStartOffset(0);
      return;
    }

    isDropPendingRef.current = true;

    if (onTaskDrop && currentTask.operatorId) {
      onTaskDrop(currentTask.id, currentTask.operatorId, operatorId, currentTask, currentPosition.time);
    }

    setIsTouchDragging(false);

    if ('vibrate' in navigator) {
      navigator.vibrate(20);
    }
  }, [isTouchDragging, onTaskDrop]);

  const handleTouchCancel = useCallback(() => {
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }
    draggedTaskRef.current = null;
    draggedTaskPositionRef.current = null;
    isDropPendingRef.current = false;
    setIsTouchDragging(false);
    setDraggedTask(null);
    setDraggedTaskPosition(null);
    setDragStartOffset(0);
  }, []);

  return {
    draggedTask,
    draggedTaskPosition,
    isDragRollback,
    isTouchDragging,
    dragStartOffset,
    scrollContainerRef,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    canReassignTask,
    rollbackDraggedTask
  };
}
